import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/dashboard/admin-sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/theme-toggle";
import { Search, Loader2, ArrowUpDown, CheckCircle, Play, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { formatPlatformCurrencyExact } from "@/lib/currency";
import { useToast } from "@/hooks/use-toast";

interface AdminTransaction {
  _id?: string;
  id?: string;
  investorId: string;
  type: string;
  amount: string;
  status: string;
  createdAt: string;
  referenceNumber?: string;
  reference?: string;
  paymentMethod?: string;
  notes?: string;
}

interface Investor {
  _id?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  clientId?: string;
}

export default function AdminTransactionsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedInvestorId, setSelectedInvestorId] = useState("");
  const [txnType, setTxnType] = useState("deposit");
  const [txnStatus, setTxnStatus] = useState("completed");

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: transactions, isLoading } = useQuery<AdminTransaction[]>({
    queryKey: ["/api/admin/transactions"],
  });

  const { data: investors } = useQuery<Investor[]>({
    queryKey: ["/api/admin/investors"],
  });

  const verifyMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("PUT", `/api/admin/transactions/${id}/verify`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/transactions"] });
      toast({ title: "Transaction verified" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to verify", description: e.message, variant: "destructive" });
    },
  });

  const processMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("PUT", `/api/admin/transactions/${id}/process`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/transactions"] });
      toast({ title: "Transaction processed" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to process", description: e.message, variant: "destructive" });
    },
  });

  const addTxnMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/transactions", data);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create transaction");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Transaction Added", description: "Transaction recorded successfully." });
      setShowAddDialog(false);
      setSelectedInvestorId("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/transactions"] });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const filtered = (transactions || []).filter((t) => {
    const q = searchQuery.toLowerCase();
    const ref = t.referenceNumber || t.reference || "";
    return (
      t.investorId?.toLowerCase().includes(q) ||
      t.type?.toLowerCase().includes(q) ||
      t.status?.toLowerCase().includes(q) ||
      ref.toLowerCase().includes(q)
    );
  });

  const formatAmount = (amount: string) => {
    const n = parseFloat(amount);
    if (Number.isNaN(n)) return amount;
    return formatPlatformCurrencyExact(n);
  };

  const sidebarStyle = {
    "--sidebar-width": "17rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <AdminSidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <header className="flex items-center justify-between gap-4 h-16 px-6 border-b bg-background/95 backdrop-blur shrink-0">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-5 h-5 text-accent" />
                <div>
                  <h1 className="text-lg font-semibold">Transactions</h1>
                  <p className="text-sm text-muted-foreground">Verify, process, and add investor payments</p>
                </div>
              </div>
            </div>
            <ThemeToggle />
          </header>

          <main className="flex-1 p-6 space-y-6 overflow-y-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="relative max-w-sm w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by investor, type, status, txn ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button onClick={() => setShowAddDialog(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Transaction
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">All Transactions ({filtered.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Investor ID</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Type</th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Amount</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Txn ID / Ref</th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={7} className="py-16 text-center">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                          </td>
                        </tr>
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="py-16 text-center text-muted-foreground">
                            No transactions found.
                          </td>
                        </tr>
                      ) : (
                        filtered.map((txn) => {
                          const id = txn._id || txn.id || "";
                          const inv = investors?.find((i) => (i._id || i.id) === txn.investorId);
                          const clientLabel = inv?.clientId ? `${inv.firstName || ""} ${inv.lastName || ""} (${inv.clientId})` : txn.investorId;

                          return (
                            <tr key={id} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="py-3 px-4 whitespace-nowrap">
                                {new Date(txn.createdAt).toLocaleDateString("en-IN")}
                              </td>
                              <td className="py-3 px-4 font-mono text-xs">{clientLabel}</td>
                              <td className="py-3 px-4 capitalize font-medium">{txn.type}</td>
                              <td className="py-3 px-4 text-right font-bold text-accent">{formatAmount(txn.amount)}</td>
                              <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{txn.referenceNumber || txn.reference || "—"}</td>
                              <td className="py-3 px-4 text-center">
                                <Badge
                                  variant="outline"
                                  className={
                                    txn.status === "processed" || txn.status === "completed"
                                      ? "bg-green-500/10 text-green-600 border-green-500/20 capitalize"
                                      : "capitalize"
                                  }
                                >
                                  {txn.status === "processed" ? "Completed" : txn.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-2">
                                  {txn.status !== "verified" && txn.status !== "processed" && txn.status !== "completed" && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 gap-1"
                                      disabled={verifyMutation.isPending}
                                      onClick={() => verifyMutation.mutate(id)}
                                    >
                                      <CheckCircle className="w-3.5 h-3.5" />
                                      Verify
                                    </Button>
                                  )}
                                  {txn.status === "verified" && (
                                    <Button
                                      size="sm"
                                      className="h-8 gap-1"
                                      disabled={processMutation.isPending}
                                      onClick={() => processMutation.mutate(id)}
                                    >
                                      <Play className="w-3.5 h-3.5" />
                                      Process
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>

      {/* Add Transaction Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
            <DialogDescription>Record a deposit, withdrawal, or investment payment for an investor</DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              if (!selectedInvestorId) {
                toast({ title: "Select Investor", description: "Please select an investor", variant: "destructive" });
                return;
              }

              addTxnMutation.mutate({
                investorId: selectedInvestorId,
                type: txnType,
                amount: String(formData.get("amount") || "0"),
                referenceNumber: String(formData.get("referenceNumber") || ""),
                paymentMethod: String(formData.get("paymentMethod") || "RTGS"),
                status: txnStatus,
                notes: String(formData.get("notes") || ""),
                createdAt: formData.get("createdAt") ? String(formData.get("createdAt")) : undefined,
              });
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label>Select Investor *</Label>
              <Select value={selectedInvestorId} onValueChange={setSelectedInvestorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose investor..." />
                </SelectTrigger>
                <SelectContent>
                  {(investors || []).map((inv) => {
                    const invId = inv._id || inv.id || "";
                    const label = `${inv.firstName || ""} ${inv.lastName || ""}`.trim() || inv.email || invId;
                    const clientTag = inv.clientId ? ` [${inv.clientId}]` : "";
                    return (
                      <SelectItem key={invId} value={invId}>
                        {label}{clientTag}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={txnType} onValueChange={setTxnType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="withdrawal">Withdrawal</SelectItem>
                    <SelectItem value="investment">Investment</SelectItem>
                    <SelectItem value="contribution">Contribution</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount (INR) *</Label>
                <Input id="amount" name="amount" type="number" placeholder="250000" required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="referenceNumber">Txn ID / UTR Ref</Label>
                <Input id="referenceNumber" name="referenceNumber" placeholder="ICIC12026051310..." className="font-mono text-xs" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentMethod">Payment Method</Label>
                <Input id="paymentMethod" name="paymentMethod" defaultValue="RTGS" placeholder="RTGS / NEFT / IMPS" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={txnStatus} onValueChange={setTxnStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed / Processed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="createdAt">Transaction Date</Label>
                <Input id="createdAt" name="createdAt" type="date" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes / Remarks</Label>
              <Input id="notes" name="notes" placeholder="Optional notes..." />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={addTxnMutation.isPending}>
                {addTxnMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Transaction
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
